/**
 * Typed bindings for /api/own-chats — the groups and channels the company's
 * accounts run: create one from an account, then manage it.
 *
 * Every change is made in Telegram while the request waits (several seconds:
 * the account connects, acts, disconnects). The chats themselves are kept on
 * the server; the people in a chat are not — each list of them is read from
 * Telegram when its tab is opened, and re-read only on request.
 */

import {
  type QueryClient,
  type UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import i18n from "@/i18n";
import { type AccountSummary, formatAccountNo } from "./accounts";
import { ApiError, api } from "./api";

export type OwnChatKind = "channel" | "group";

export const ADMIN_RIGHT_KEYS = [
  "change_info",
  "post_messages",
  "edit_messages",
  "delete_messages",
  "ban_users",
  "invite_users",
  "pin_messages",
  "add_admins",
  "anonymous",
] as const;
export type AdminRight = (typeof ADMIN_RIGHT_KEYS)[number];
export type AdminRights = Record<AdminRight, boolean>;

/** The rights that mean something in each kind of chat, in Telegram's order. */
export const ADMIN_RIGHTS: Record<OwnChatKind, AdminRight[]> = {
  channel: [
    "change_info",
    "post_messages",
    "edit_messages",
    "delete_messages",
    "invite_users",
    "add_admins",
  ],
  group: [
    "change_info",
    "delete_messages",
    "ban_users",
    "invite_users",
    "pin_messages",
    "add_admins",
    "anonymous",
  ],
};

/** What a new admin gets unless the operator unticks it: everything but
 *  appointing other admins and hiding behind the chat's name. */
export function defaultAdminRights(kind: OwnChatKind): AdminRights {
  const rights = Object.fromEntries(ADMIN_RIGHT_KEYS.map((k) => [k, false])) as AdminRights;
  for (const key of ADMIN_RIGHTS[kind]) {
    rights[key] = key !== "add_admins" && key !== "anonymous";
  }
  return rights;
}

export const PERMISSION_KEYS = [
  "send_messages",
  "send_media",
  "send_polls",
  "embed_links",
  "invite_users",
  "pin_messages",
  "change_info",
] as const;
export type Permission = (typeof PERMISSION_KEYS)[number];
export type Permissions = Record<Permission, boolean>;

/** The pauses between two messages of one member Telegram offers, seconds. */
export const SLOW_MODE_CHOICES = [0, 10, 30, 60, 300, 900, 3600] as const;

/** Telegram's own limits (app.core.own_chats). */
export const LIMITS = { title: 128, about: 255, rank: 16, post: 4096, caption: 1024 } as const;

export interface OwnChat {
  id: string;
  account_id: string;
  account_seq_no: number;
  account_label: string;
  kind: OwnChatKind;
  title: string;
  about: string | null;
  /** Public name; null = closed, reached only by an invite link. */
  username: string | null;
  invite_link: string | null;
  /** Owner, as opposed to one of the admins. */
  is_creator: boolean;
  members_count: number | null;
  /** Groups only: what an ordinary member may do. */
  permissions: Permissions | null;
  slow_mode_seconds: number;
  /** A channel's discussion group / a group's channel; `id` is null when it
   *  is not one of the listed chats. */
  linked: { id: string | null; title: string | null } | null;
  requests_pending: number;
  created_at: string;
  /** When this was last read from Telegram; null = never in full. */
  synced_at: string | null;
  /** A channel's second admin: waiting · promoting · done · failed; null —
   *  none was asked for. When its next step is due, the account, why it failed. */
  second_admin_state: "waiting" | "promoting" | "done" | "failed" | null;
  second_admin_due_at: string | null;
  second_admin_seq_no: number | null;
  second_admin_detail: string | null;
}

export interface CreatedOwnChat {
  chat: OwnChat;
  /** The chat was created, but a later step was not done — and why. */
  warning: string | null;
}

/** A person as an action on them names them. */
export interface UserRef {
  tg_user_id: number;
  /** The acting account's key for the person, as text (wider than a JS number). */
  access_hash: string;
}

export interface Person {
  tg_user_id: number;
  access_hash: string | null;
  name: string;
  username: string | null;
  is_bot: boolean;
  is_self: boolean;
  role: "creator" | "admin" | "member" | "banned" | "requested";
  rights: AdminRights | null;
  rank: string | null;
  /** A join request: what the person wrote, and when. */
  about: string | null;
  since: string | null;
  /** Set when this is one of the company's own accounts. */
  account_seq_no: number | null;
}

export interface InviteLink {
  link: string;
  title: string | null;
  is_main: boolean;
  request_needed: boolean;
  joined: number;
  usage_limit: number | null;
  requests_pending: number;
  expires_at: string | null;
}

/** Who becomes an admin: exactly one of the three. */
export type AdminTarget =
  | { account_id: string }
  | { username: string }
  | { user: UserRef };

export function refOf(person: Person): UserRef | null {
  return person.access_hash === null
    ? null
    : { tg_user_id: person.tg_user_id, access_hash: person.access_hash };
}

/** Where the chat opens in Telegram: its public page, else its invite link. */
export function chatUrl(chat: Pick<OwnChat, "username" | "invite_link">): string | null {
  return chat.username ? `https://t.me/${chat.username}` : chat.invite_link;
}

/**
 * An account that can act in Telegram right now, as far as its row tells: in
 * work or warming up, on a proxy that answers, not sitting out a FloodWait.
 * The server checks the same before connecting, and says which it was.
 */
export function canAct(account: AccountSummary): boolean {
  if (account.status !== "active" && account.status !== "warming_up") return false;
  if (account.proxy_id === null || account.proxy_is_alive === false) return false;
  return !(account.flood_wait_until && Date.parse(account.flood_wait_until) > Date.now());
}

/** "#064 · name", and a mark on an account still in its warmup. */
export function accountOptionLabel(account: AccountSummary, notWarmed: string): string {
  const name = `${formatAccountNo(account.seq_no)} · ${account.display_name}`;
  return account.warmup_completed ? name : `${name} · ${notWarmed}`;
}

/** Telegram's refusal as the server worded it, or the caller's own words. */
export function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.detail : fallback;
}

/**
 * A request that got no answer at all — the connection dropped, the server is
 * restarting — becomes an error with words, like any refusal. These requests
 * last many seconds and change things in Telegram: silence would read as
 * "done", and it may or may not be.
 */
async function answered<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, i18n.t("own_chats.no_connection"), null);
  }
}

/**
 * The same change asked for twice at once is one change. A busy button is not
 * enough of a guard: the second click of a double click lands before the
 * button has been redrawn as busy, and the server would do the thing again —
 * a second channel, the post published twice. Both callers get the one answer.
 */
const underWay = new Map<string, Promise<unknown>>();

function once<T>(key: string, send: () => Promise<T>): Promise<T> {
  const running = underWay.get(key) as Promise<T> | undefined;
  if (running) return running;
  const sent = answered(send()).finally(() => underWay.delete(key));
  underWay.set(key, sent);
  return sent;
}

const tg = {
  get: <T>(path: string) => answered(api.get<T>(path)),
  post: <T>(path: string, json?: unknown) =>
    once(`POST ${path} ${JSON.stringify(json ?? null)}`, () => api.post<T>(path, json)),
  put: <T>(path: string, json?: unknown) =>
    once(`PUT ${path} ${JSON.stringify(json ?? null)}`, () => api.put<T>(path, json)),
  patch: <T>(path: string, json?: unknown) =>
    once(`PATCH ${path} ${JSON.stringify(json ?? null)}`, () => api.patch<T>(path, json)),
  delete: <T>(path: string) => once(`DELETE ${path}`, () => api.delete<T>(path)),
  /** `what` names the form's contents: a form cannot be compared with another. */
  upload: <T>(path: string, form: FormData, what: string) =>
    once(`UPLOAD ${path} ${what}`, () => api.upload<T>(path, form)),
};

/** A file by what tells it from another, for telling two uploads apart. */
function fileKey(file: File | null): string {
  return file ? `${file.name}:${file.size}:${file.lastModified}` : "-";
}

/**
 * Run a mutation from a confirm dialog: done when it settles, either way. A
 * failure is not thrown at the dialog — nobody would catch it there — and is
 * reported by the app's fallback for errors no caller has shown.
 *
 * Through `mutateAsync`, not `mutate` with callbacks: those fire only for the
 * latest call of a hook, and a promise waiting on them could wait for ever.
 */
export function settle<TData, TVars>(
  mutation: UseMutationResult<TData, Error, TVars>,
  vars: TVars,
  onSuccess?: (data: TData) => void,
): Promise<void> {
  return mutation.mutateAsync(vars).then(
    (data) => onSuccess?.(data),
    () => undefined,
  );
}

export const OWN_CHATS_KEY = ["own_chats"] as const;
const peopleKey = (id: string, what: string) => ["own_chats", id, what] as const;

/** Lists read from Telegram are kept while the card may come back to them. */
const LIVE_KEPT_MS = 30 * 60_000;

/** How often the list is read again while a channel's second admin is on its
 *  way — so that what the account did shows without reopening the page. */
const SECOND_ADMIN_REFRESH_MS = 15_000;

export function useOwnChats() {
  return useQuery<OwnChat[]>({
    queryKey: OWN_CHATS_KEY,
    queryFn: () => tg.get<OwnChat[]>("/api/own-chats"),
    refetchInterval: (query) =>
      query.state.data?.some(
        (c) => c.second_admin_state === "waiting" || c.second_admin_state === "promoting",
      )
        ? SECOND_ADMIN_REFRESH_MS
        : false,
  });
}

/** Put a chat an endpoint answered with into the list, as it came. */
async function putChat(qc: QueryClient, chat: OwnChat) {
  // A read of the list sent before this change must not land on top of it. If
  // one was on its way it is asked for again afterwards: it may have been
  // another action's — a delete's, say — and must not be lost with this one.
  const wasReading = qc.isFetching({ queryKey: OWN_CHATS_KEY, exact: true }) > 0;
  await qc.cancelQueries({ queryKey: OWN_CHATS_KEY, exact: true });
  qc.setQueryData<OwnChat[]>(OWN_CHATS_KEY, (list) =>
    list?.map((c) => (c.id === chat.id ? chat : c)),
  );
  if (wasReading) qc.invalidateQueries({ queryKey: OWN_CHATS_KEY, exact: true });
}

/** A change that answers with the chat. */
function useChatChange<TVars extends { id: string }>(run: (vars: TVars) => Promise<OwnChat>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: run, onSuccess: (chat) => putChat(qc, chat) });
}

export function useCreateOwnChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      account_id: string;
      kind: OwnChatKind;
      title: string;
      about: string;
      username: string | null;
    }) => tg.post<CreatedOwnChat>("/api/own-chats", payload),
    onSuccess: async ({ chat }) => {
      // Into the list as it came — its card opens at once, on this very row —
      // and only then is the list read again.
      await qc.cancelQueries({ queryKey: OWN_CHATS_KEY, exact: true });
      qc.setQueryData<OwnChat[]>(OWN_CHATS_KEY, (list) => [
        chat,
        ...(list ?? []).filter((c) => c.id !== chat.id),
      ]);
      qc.invalidateQueries({ queryKey: OWN_CHATS_KEY, exact: true });
    },
  });
}

/** Take in the chats an account already owns or administers. */
export function useDiscoverOwnChats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (account_id: string) =>
      tg.post<{ found: number; added: number }>("/api/own-chats/actions/discover", {
        account_id,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: OWN_CHATS_KEY, exact: true }),
  });
}

/**
 * A chat taken in from an account's chat list is known by little: the rest is
 * read the first time its card opens. A query, not a mutation fired from an
 * effect — it is asked for once however many times the card mounts, and its
 * "reading" ends when the answer comes whichever mount is looking. A read that
 * failed is not tried again by a remount either: "Refresh" is there for that.
 */
export function useFirstRead(chat: OwnChat) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["own_chats", chat.id, "first_read"],
    enabled: chat.synced_at === null,
    staleTime: Infinity,
    retryOnMount: false,
    queryFn: async () => {
      const read = await tg.post<OwnChat>(`/api/own-chats/${chat.id}/refresh`);
      await putChat(qc, read);
      return read.synced_at;
    },
  });
}

export function useRefreshOwnChat() {
  return useChatChange(({ id }: { id: string }) =>
    tg.post<OwnChat>(`/api/own-chats/${id}/refresh`),
  );
}

export function useUpdateOwnChat() {
  return useChatChange(({ id, ...patch }: { id: string; title?: string; about?: string }) =>
    tg.patch<OwnChat>(`/api/own-chats/${id}`, patch),
  );
}

/** The ways into a chat and the people waiting behind them. */
export interface Entrance {
  links: InviteLink[];
  requests: Person[];
}

const entranceKey = (id: string) => peopleKey(id, "entrance");

/** The chat's main link, as an answer just gave it, into the list of links held. */
function putMainLink(qc: QueryClient, chat: OwnChat) {
  const link = chat.invite_link;
  if (link === null) return;
  qc.setQueryData<Entrance>(entranceKey(chat.id), (held) => {
    if (!held || held.links.some((l) => l.is_main && l.link === link)) return held;
    const main: InviteLink = {
      link,
      title: null,
      is_main: true,
      request_needed: false,
      joined: 0,
      usage_limit: null,
      requests_pending: 0,
      expires_at: null,
    };
    return { ...held, links: [main, ...held.links.filter((l) => !l.is_main)] };
  });
}

/** Open the chat under a public name; null closes it. */
export function useSetOwnChatUsername() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, username }: { id: string; username: string | null }) =>
      tg.put<OwnChat>(`/api/own-chats/${id}/username`, { username }),
    onSuccess: async (chat) => {
      await putChat(qc, chat);
      // Closing a chat that had no link gives it one: the links held must show
      // it, or "issue a link" would be offered and stop the one just made.
      putMainLink(qc, chat);
    },
  });
}

/** Put a picture up as the chat's photo; null takes the photo down. */
export function useSetOwnChatPhoto() {
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File | null }) => {
      const form = new FormData();
      if (file) form.append("file", file);
      return tg.upload<void>(`/api/own-chats/${id}/photo`, form, fileKey(file));
    },
  });
}

export function useSetOwnChatRules() {
  return useChatChange(
    ({ id, ...rules }: { id: string; permissions: Permissions; slow_mode_seconds: number }) =>
      tg.put<OwnChat>(`/api/own-chats/${id}/rules`, rules),
  );
}

/** Issue the main invite link anew; the previous one stops working. */
export function useRenewMainLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      tg.post<OwnChat>(`/api/own-chats/${id}/invite-links/main`),
    onSuccess: async (chat) => {
      await putChat(qc, chat);
      putMainLink(qc, chat);
    },
  });
}

/** Attach one of the same account's groups to a channel; null detaches. */
export function useSetDiscussion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, group_id }: { id: string; group_id: string | null }) =>
      tg.put<OwnChat>(`/api/own-chats/${id}/discussion`, { group_id }),
    // Two rows change, the channel's and the group's: read the list again.
    onSuccess: () => qc.invalidateQueries({ queryKey: OWN_CHATS_KEY, exact: true }),
  });
}

/** Mark the member lists held as out of date — read anew when next opened, not now. */
function staleMembers(qc: QueryClient, id: string, only?: (banned: boolean) => boolean) {
  const prefix = peopleKey(id, "members");
  qc.invalidateQueries({
    refetchType: "none",
    predicate: ({ queryKey }) =>
      prefix.every((part, i) => queryKey[i] === part) &&
      (only === undefined || only(queryKey[4] === true)),
  });
}

export function useTransferOwnChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, user }: { id: string; user: UserRef }) =>
      tg.post<OwnChat>(`/api/own-chats/${id}/transfer`, { user }),
    onSuccess: (_chat, { id }) => {
      qc.invalidateQueries({ queryKey: OWN_CHATS_KEY, exact: true });
      qc.invalidateQueries({ queryKey: peopleKey(id, "admins") });
      staleMembers(qc, id);
    },
  });
}

export interface Cleanup {
  removed: number;
  /** Lines Telegram would not delete. */
  refused: number;
  /** The "chat created" line stays: Telegram deletes it for nobody. */
  creation_kept: boolean;
  /** How many messages, newest first, were looked through; `whole` — all there are. */
  looked: number;
  whole: boolean;
  /** Some lines were removed, then Telegram stopped the rest — and why. */
  warning: string | null;
}

/** Delete the chat's service lines — "photo updated", "pinned …", "joined" —
 *  a few at a time with pauses: seconds, or a couple of minutes. */
/** Give a channel a second admin now: the account walks in within a minute. */
export function useBringSecondAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => tg.post<OwnChat>(`/api/own-chats/${id}/second-admin`),
    onSuccess: () => qc.invalidateQueries({ queryKey: OWN_CHATS_KEY }),
  });
}

export function useRemoveServiceLines() {
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      tg.post<Cleanup>(`/api/own-chats/${id}/service-lines/remove`),
  });
}

export function useDeleteOwnChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, in_telegram }: { id: string; in_telegram: boolean }) =>
      tg.delete<void>(`/api/own-chats/${id}?in_telegram=${in_telegram}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: OWN_CHATS_KEY, exact: true }),
  });
}

/* ------------------------------------------------------------------ people */

/**
 * A list read from Telegram: kept until asked for again, never re-read by
 * itself — not by coming back to its tab, not after a read that failed.
 */
function useLive<T>(key: readonly unknown[], path: string, enabled: boolean) {
  return useQuery<T>({
    queryKey: key,
    queryFn: () => tg.get<T>(path),
    enabled,
    staleTime: Infinity,
    gcTime: LIVE_KEPT_MS,
    retryOnMount: false,
  });
}

export function useChatAdmins(id: string, enabled: boolean) {
  return useLive<Person[]>(peopleKey(id, "admins"), `/api/own-chats/${id}/admins`, enabled);
}

/**
 * What the admins list now says, put onto the member lists held: a second trip
 * to Telegram just to redraw one row is a trip too many.
 */
function putAdmins(qc: QueryClient, id: string, admins: Person[]) {
  qc.setQueryData(peopleKey(id, "admins"), admins);
  const byId = new Map(admins.map((a) => [a.tg_user_id, a]));
  qc.setQueriesData<Person[]>({ queryKey: peopleKey(id, "members") }, (list) =>
    list?.map((p) => {
      if (p.role === "banned") return p;
      const admin = byId.get(p.tg_user_id);
      if (admin) return { ...p, role: admin.role, rights: admin.rights, rank: admin.rank };
      return p.role === "admin" ? { ...p, role: "member", rights: null, rank: null } : p;
    }),
  );
}

export function useSetChatAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: { id: string; rights: AdminRights; rank: string } & AdminTarget) =>
      tg.post<Person[]>(`/api/own-chats/${id}/admins`, body),
    onSuccess: (admins, { id }) => putAdmins(qc, id, admins),
  });
}

export function useRemoveChatAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, user }: { id: string; user: UserRef }) =>
      tg.post<Person[]>(`/api/own-chats/${id}/admins/remove`, { user }),
    onSuccess: (admins, { id }) => putAdmins(qc, id, admins),
  });
}

export function useChatMembers(
  id: string,
  filter: { q: string; banned: boolean },
  enabled: boolean,
) {
  const params = new URLSearchParams({ q: filter.q, banned: String(filter.banned) });
  return useLive<Person[]>(
    [...peopleKey(id, "members"), filter.q, filter.banned],
    `/api/own-chats/${id}/members?${params}`,
    enabled,
  );
}

/** kick — out, free to come back; ban — out for good; unban — may come back. */
export function useMoveChatMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      user,
      action,
    }: {
      id: string;
      user: UserRef;
      action: "kick" | "ban" | "unban";
    }) =>
      action === "unban"
        ? tg.post<void>(`/api/own-chats/${id}/members/unban`, { user })
        : tg.post<void>(`/api/own-chats/${id}/members/remove`, { user, mode: action }),
    onSuccess: (_out, { id, user, action }) => {
      const gone = (list: Person[] | undefined) =>
        list?.filter((p) => p.tg_user_id !== user.tg_user_id);
      // Out of every list of members held — without asking Telegram again.
      qc.setQueriesData<Person[]>({ queryKey: peopleKey(id, "members") }, gone);
      if (action === "unban") return;
      // Thrown out, they are no admin either.
      qc.setQueryData<Person[]>(peopleKey(id, "admins"), gone);
      qc.invalidateQueries({ queryKey: OWN_CHATS_KEY, exact: true });
      // The banned lists held do not have them yet.
      if (action === "ban") staleMembers(qc, id, (banned) => banned);
    },
  });
}

/** Both lists of the "links and requests" tab, in one connection. */
export function useChatEntrance(id: string, enabled: boolean) {
  const qc = useQueryClient();
  return useQuery<Entrance>({
    queryKey: entranceKey(id),
    enabled,
    staleTime: Infinity,
    gcTime: LIVE_KEPT_MS,
    retryOnMount: false,
    queryFn: async () => {
      const read = await tg.get<Entrance>(`/api/own-chats/${id}/entrance`);
      // The server noted the same on the chat's row: how many wait, and the
      // main link as Telegram lists it now.
      const main = read.links.find((l) => l.is_main)?.link;
      qc.setQueryData<OwnChat[]>(OWN_CHATS_KEY, (list) =>
        list?.map((c) =>
          c.id === id
            ? { ...c, requests_pending: read.requests.length, invite_link: main ?? c.invite_link }
            : c,
        ),
      );
      return read;
    },
  });
}

/** Put the links the server answered with in place, without asking Telegram again. */
function putLinks(qc: QueryClient, id: string, links: InviteLink[]) {
  qc.setQueryData<Entrance>(entranceKey(id), (held) => held && { ...held, links });
}

export function useCreateInviteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      title: string;
      request_needed: boolean;
      usage_limit: number | null;
    }) => tg.post<InviteLink[]>(`/api/own-chats/${id}/invite-links`, body),
    onSuccess: (links, { id }) => putLinks(qc, id, links),
  });
}

export function useRevokeInviteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, link }: { id: string; link: string }) =>
      tg.post<InviteLink[]>(`/api/own-chats/${id}/invite-links/revoke`, { link }),
    onSuccess: (links, { id }) => putLinks(qc, id, links),
  });
}

/** Let one person in or turn them away; without `user` — everybody waiting. */
export function useAnswerJoinRequests() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; user?: UserRef; approve: boolean }) =>
      tg.post<Person[]>(`/api/own-chats/${id}/join-requests/answer`, body),
    onSuccess: (requests, { id, approve }) => {
      qc.setQueryData<Entrance>(
        entranceKey(id),
        (held) =>
          held && {
            requests,
            // With nobody left waiting, no link has anybody waiting behind it.
            links:
              requests.length === 0
                ? held.links.map((l) => ({ ...l, requests_pending: 0 }))
                : held.links,
          },
      );
      qc.invalidateQueries({ queryKey: OWN_CHATS_KEY, exact: true });
      // The people let in are members now; the lists held do not have them.
      if (approve) staleMembers(qc, id, (banned) => !banned);
    },
  });
}

/* ------------------------------------------------------------------- posts */

export interface Posted {
  message_id: number;
  url: string;
  /** The post is out, but something asked along with it — the pin — was not done. */
  warning: string | null;
}

export function usePostToOwnChat() {
  return useMutation({
    mutationFn: ({
      id,
      text,
      pin,
      file,
    }: {
      id: string;
      text: string;
      pin: boolean;
      file: File | null;
    }) => {
      const form = new FormData();
      form.append("text", text);
      form.append("pin", String(pin));
      if (file) form.append("file", file);
      return tg.upload<Posted>(
        `/api/own-chats/${id}/posts`,
        form,
        `${pin}:${fileKey(file)}:${text}`,
      );
    },
  });
}
